import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateLumpsum, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const LumpsumCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [principal, setPrincipal] = useState(100000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [years, setYears] = useState(10);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, expectedReturn, years]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(expectedReturn) || 0;
    const y = Number(years) || 0;
    const lumpsumResult = calculateLumpsum(p, r, y);
    setResult(lumpsumResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('Lumpsum Investment Report', {
      'Investment Amount': result.totalInvested,
      'Expected Return': `${expectedReturn}% p.a.`,
      'Investment Period': `${years} years`,
      'Estimated Returns': result.wealthGained,
      'Total Value': result.futureValue,
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setExpectedReturn(12);
    setYears(10);
  };

  const chartData = result ? {
    labels: ['Invested Amount', 'Est. Returns'],
    datasets: [{
      data: [result.totalInvested, result.wealthGained],
      backgroundColor: ['#2563eb', '#f59e0b'],
      borderColor: ['#1d4ed8', '#d97706'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Lumpsum Calculator"
        description="Free Lumpsum Investment Calculator - Calculate returns on one-time mutual fund investment. See wealth growth with compound interest."
        keywords="lumpsum calculator, lumpsum investment calculator, one-time investment calculator, mutual fund lumpsum, lumpsum returns calculator"
        canonicalPath="/lumpsum"
      />
      <div className="calculator-header">
        <h1>Lumpsum Calculator</h1>
        <p>Calculate returns on one-time mutual fund investments</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="10000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((principal || 0) / 10000000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Expected Return Rate (% p.a.)</label>
            <input
              type="number"
              value={expectedReturn === '' ? '' : expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="30"
              step="0.5"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={expectedReturn || 0}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((expectedReturn || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={years === '' ? '' : years}
              onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="40"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="40"
                step="1"
                value={years || 0}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((years || 0) / 40) * 100}%` }}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Total Value</h3>
                <div className="amount">{formatCurrency(result.futureValue)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Invested Amount</span>
                  <span className="value">{formatCurrency(result.totalInvested)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Est. Returns</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.wealthGained)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Returns Multiple</span>
                  <span className="value">
                    {(result.futureValue / result.totalInvested).toFixed(2)}x
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Lumpsum Investment</h4>
        <p>
          Lumpsum investment is when you invest a large amount at once instead of regular intervals.
          The future value is calculated using compound interest formula:
        </p>
        <p style={{ marginTop: '8px', fontFamily: 'monospace' }}>
          FV = P × (1 + r)^n
        </p>
        <p style={{ marginTop: '8px' }}>
          Where P = Principal, r = Annual return rate, n = Number of years
        </p>
      </div>
    </div>
  );
};

export default LumpsumCalculator;
